import { apiDownload, apiFetch, apiUpload } from './client';
import type {
  Activity,
  AssignableUser,
  Comment,
  Company,
  CompanyLookup,
  CompanyRelated,
  Deal,
  DealAttachment,
  DealSite,
  EmailMessageBody,
  HotelSite,
  Issue,
  IssueAttachment,
  IssueComment,
  OwnerRollup,
  Paginated,
  Person
} from '$lib/types';

export type ListParams = Record<string, string | number | undefined>;

function toQuery(params: ListParams = {}): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  const s = search.toString();
  return s ? `?${s}` : '';
}

function crudApi<T>(basePath: string) {
  return {
    list: (params?: ListParams) => apiFetch<Paginated<T>>(`${basePath}${toQuery(params)}`),
    get: (id: number) => apiFetch<T>(`${basePath}/${id}`),
    create: (body: Record<string, unknown>) =>
      apiFetch<T>(basePath, { method: 'POST', body: JSON.stringify(body) }),
    update: (id: number, body: Record<string, unknown>) =>
      apiFetch<T>(`${basePath}/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
    remove: (id: number) => apiFetch<{ detail: string }>(`${basePath}/${id}`, { method: 'DELETE' })
  };
}

export const companiesApi = {
  ...crudApi<Company>('/companies'),
  // Single round trip for the record-detail view's owner/brand/people/deals/
  // activities/issues/properties sections — see companyRelated on the backend.
  getWithRelated: (id: number) =>
    apiFetch<Company & { related: CompanyRelated }>(`/companies/${id}?include=related`),
  // The Hotels "owner" company-detail rollup (see companyRollup on the
  // backend) — every site the owner has, direct or via a brand it owns, plus
  // cross-site open issues/activities and aggregates.
  rollup: (id: number) => apiFetch<OwnerRollup>(`/companies/${id}/rollup`)
};
// The minimal, issues-only-safe company read (id/name/kind) — used to
// populate the issue form's company picker and to drive the client-side
// "unit required for hotel-kind companies" rule. See companiesLookup on the
// backend.
export const companiesLookupApi = {
  list: (params?: { q?: string; kind?: string; vertical_id?: number; per_page?: number }) =>
    apiFetch<{ results: CompanyLookup[] }>(`/companies/lookup${toQuery(params)}`)
};
// Quick-create counterpart to the lookup above — also open to issues_only
// accounts (see companiesLookup / quickCreateCompany on the backend).
// vertical_id is a query param, not a body field: the server resolves it
// against the caller's accessible verticals rather than trusting the client
// (a member never needs to pass it; an admin, who has more than one
// accessible vertical, must).
export const companiesQuickCreateApi = {
  create: (body: { name: string; kind: string; city?: string }, verticalId?: number) =>
    apiFetch<CompanyLookup>(`/companies/quick${toQuery({ vertical_id: verticalId })}`, {
      method: 'POST',
      body: JSON.stringify(body)
    })
};
export interface ContactOwner {
  id: number;
  name: string;
  email: string;
  vertical_ids: number[];
  vertical_id?: number;
  vertical_name?: string;
}
export interface OwnershipHistory {
  from_owner_name?: string | null;
  to_owner_name?: string | null;
  actor_name?: string | null;
  id: number;
  from_owner_user_id: number | null;
  to_owner_user_id: number | null;
  from_vertical_id: number;
  to_vertical_id: number;
  note: string;
  created_at: string;
  actor_user_id: number;
}
export interface ContactTransfer {
  owner_user_id: number;
  vertical_id: number;
  note: string;
  move_open_deals: boolean;
}
// Salespeople an intake coordinator can hand work to (primary pipeline in the
// caller's scope), with today's IST meeting load. Read model only — ownership
// still changes through transfer().
export interface HandoverTarget {
  user_id: number;
  name: string;
  vertical_id: number;
  vertical_name: string;
  meetings_today: number;
  next_meeting_at: string | null;
  handed_over_today: number;
}
export const peopleApi = {
  archive: (id: number, verticalId: number, reason = '') => apiFetch<unknown>(`/people/${id}/archive`, { method: 'POST', body: JSON.stringify({ vertical_id: verticalId, reason }) }),
  restore: (id: number, verticalId: number) => apiFetch<unknown>(`/people/${id}/restore`, { method: 'POST', body: JSON.stringify({ vertical_id: verticalId }) }),
  salesOwners: () => apiFetch<{ results: ContactOwner[] }>('/people/sales-owners'),
  ...crudApi<Person>('/people'),
  handoverTargets: () => apiFetch<{ results: HandoverTarget[] }>('/people/handover-targets'),
  owners: (verticalId: number) => apiFetch<{ results: ContactOwner[] }>(`/people/owners${toQuery({ vertical_id: verticalId })}`),
  ownershipHistory: (id: number) => apiFetch<{ results: OwnershipHistory[] }>(`/people/${id}/ownership-history`),
  transfer: (id: number, body: ContactTransfer) => apiFetch<unknown>(`/people/${id}/transfer`, { method: 'POST', body: JSON.stringify(body) })
};
export const dealsApi = crudApi<Deal>('/deals');
// The "Sites" view's single aggregate read (see listSites on the backend) —
// vertical_id is required, the row shape depends on the vertical (hotels:
// HotelSite; everything else: DealSite), so the caller picks which to read
// based on the selected vertical's slug. Editing a site's own phase/status
// still goes through companiesApi.update / dealsApi.update, not this.
export const sitesApi = {
  list: (params: { vertical_id: number; q?: string }) =>
    apiFetch<{ results: (HotelSite | DealSite)[] }>(`/sites${toQuery(params)}`),
  // Single-site read backing the full-page site dashboard (see getSite on
  // the backend) — same row shape as one element of list()'s results.
  get: (id: number, verticalId: number) =>
    apiFetch<HotelSite | DealSite>(`/sites/${id}${toQuery({ vertical_id: verticalId })}`)
};
export const activitiesApi = crudApi<Activity>('/activities');
// Raw .eml download for an auto-logged email touchpoint (see the linked
// Activity.email.has_raw — only present when the raw copy was actually
// stored in S3).
export const emailMessagesApi = {
  download: (emailMessageId: number, filename: string) =>
    apiDownload(`/email-messages/${emailMessageId}/download`, filename),
  // Full parsed text/html body for the review-in-CRM viewer — 404 when the
  // raw email was never stored (same has_raw gate as download()).
  body: (emailMessageId: number) => apiFetch<EmailMessageBody>(`/email-messages/${emailMessageId}/body`)
};
export const issuesApi = {
  ...crudApi<Issue>('/issues'),
  // Distinct unit values recorded against a company's issues — feeds the
  // unit combobox's suggestions (free entry is still allowed).
  units: (companyId: number) => apiFetch<{ results: string[] }>(`/issues/units?company_id=${companyId}`),
  // Hands the issue off to Genba as a ticket (see internal/api/handlers/
  // issues.go's sendIssueToDev). One-shot: 409 if already sent, 503 if
  // GENBA_URL/GENBA_TOKEN aren't configured, 502 on any Genba-side failure.
  sendToDev: (id: number) => apiFetch<Issue>(`/issues/${id}/send-to-dev`, { method: 'POST' })
};

export const issueCommentsApi = {
  list: (issueId: number) => apiFetch<{ results: IssueComment[] }>(`/issues/${issueId}/comments`),
  create: (issueId: number, body: string) =>
    apiFetch<IssueComment>(`/issues/${issueId}/comments`, {
      method: 'POST',
      body: JSON.stringify({ body })
    })
};

export const issueAttachmentsApi = {
  list: (issueId: number) => apiFetch<{ results: IssueAttachment[] }>(`/issues/${issueId}/attachments`),
  upload: (issueId: number, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return apiUpload<IssueAttachment>(`/issues/${issueId}/attachments`, form);
  },
  remove: (attachmentId: number) =>
    apiFetch<{ detail: string }>(`/attachments/${attachmentId}`, { method: 'DELETE' })
};

export const dealAttachmentsApi = {
  list: (dealId: number) => apiFetch<{ results: DealAttachment[] }>(`/deals/${dealId}/attachments`),
  upload: (dealId: number, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return apiUpload<DealAttachment>(`/deals/${dealId}/attachments`, form);
  },
  download: (dealId: number, attachment: DealAttachment) =>
    apiDownload(`/deals/${dealId}/attachments/${attachment.id}/download`, attachment.file_name),
  remove: (dealId: number, attachmentId: number) =>
    apiFetch<{ detail: string }>(`/deals/${dealId}/attachments/${attachmentId}`, { method: 'DELETE' })
};

export const assignableUsersApi = {
  list: () => apiFetch<{ results: AssignableUser[] }>('/users/assignable')
};

// Freeform comment thread shared by leads and companies (see
// internal/api/handlers/comments.go). Author-or-admin delete is enforced
// server-side; the client shows the delete button only for the caller's own
// comments (see CommentThread.svelte).
export const commentsApi = {
  list: (entityType: 'lead' | 'company', entityId: number) =>
    apiFetch<{ results: Comment[] }>(`/comments${toQuery({ entity_type: entityType, entity_id: entityId })}`),
  create: (entityType: 'lead' | 'company', entityId: number, body: string) =>
    apiFetch<Comment>('/comments', {
      method: 'POST',
      body: JSON.stringify({ entity_type: entityType, entity_id: entityId, body })
    }),
  remove: (id: number) => apiFetch<{ detail: string }>(`/comments/${id}`, { method: 'DELETE' })
};

export interface CommissionRow {
  person_id: number;
  person_name: string;
  deal_count: number;
  total_commission: number;
}

export type ReportExportType = 'companies' | 'people' | 'issues' | 'deals' | 'activities';

export const reportsApi = {
  commissions: (params?: { vertical_id?: number }) =>
    apiFetch<{ results: CommissionRow[] }>(`/reports/commissions${toQuery(params)}`),
  // Triggers a real .xlsx download — see internal/api/handlers/reports.go's
  // exportReport for the filename convention this mirrors client-side
  // (apiDownload sets the anchor's `download` attribute directly rather than
  // reading Content-Disposition, same as every other apiDownload caller).
  export: (params: { type: ReportExportType; from?: string; to?: string; vertical_id?: number }) => {
    const fromLabel = params.from || 'start';
    const toLabel = params.to || new Date().toISOString().slice(0, 10);
    return apiDownload(`/reports/export${toQuery(params)}`, `new-${params.type}-${fromLabel}_${toLabel}.xlsx`);
  }
};
